// @flow

import * as React from "react";

import ScrollContainer from "react-indiana-drag-scroll";

import addACFData from "../../utils/addACFData";
import getContentReferenceTileProps from "../../utils/getContentReferenceTileProps";
import { type LinkType } from "../../types/";
import Tile from "../molecules/Tile";
import TileCarousel from "../molecules/TileCarousel";
import Icon from "../atoms/Icon";
import TextButton from "../atoms/TextButton";

type PropsType = {
    tiles?: any[],
    containerClass?: string,
    ctaClass?: string,
    itemClass: string,
    title: string,
    cta?: LinkType,
    useContainer: boolean,
    colClass?: string,
};

const TileSliderInner = (props: PropsType): React.Node => {
    const scrollRef = React.useRef(null);
    const classes = ["tile-slider"];
    const ctaClassList = ["tile-slider__header__events"];

    const totalTiles = props.tiles ? props.tiles.length : 0;
    const [activeTile, setActiveTile] = React.useState(0);
    const [showPrevArrow, setShowPrevArrow] = React.useState(false);
    const [showNextArrow, setShowNextArrow] = React.useState(false);

    const updateScrollNavigation = () => {
        if (scrollRef.current) {
            // prev arrow state
            if (scrollRef.current.scrollLeft > 5) {
                setShowPrevArrow(true);
            } else {
                setShowPrevArrow(false);
            }

            // next arrow state
            if (totalTiles < 4) {
                setShowNextArrow(false);
            } else if (
                scrollRef.current.scrollLeft + scrollRef.current.clientWidth <
                scrollRef.current.scrollWidth
            ) {
                setShowNextArrow(true);
            } else {
                setShowNextArrow(false);
            }
        }
    };

    const scrollToActiveEl = () => {
        if (scrollRef.current) {
            const activeTileEl = scrollRef.current.querySelector(".is-active");

            if (activeTileEl) {
                const tileScrollOffset = activeTileEl.offsetLeft;

                scrollRef.current.scrollTo({
                    top: 0,
                    left: tileScrollOffset,
                    behavior: "smooth",
                });
            }
        }
    };

    const handlePrevClick = () => {
        const newIndex = activeTile - 1;
        setActiveTile(newIndex > 0 ? newIndex : 0);
    };

    const handleNextClick = () => {
        const newIndex = activeTile + 1;
        setActiveTile(newIndex < totalTiles - 1 ? newIndex : totalTiles - 1);
    };

    // initial run once use effect
    React.useEffect(() => {
        updateScrollNavigation();
    }, []);

    // updated index use effect
    React.useEffect(() => {
        scrollToActiveEl();
    }, [activeTile]);

    if (props.ctaClass) {
        ctaClassList.push(props.ctaClass);
    }

    return (
        <div className={classes.join(" ")}>
            <div className="tile-slider__header">
                {props.title && (
                    <div className="tile-slider__header__title-container"><h2 className="tile-slider__header__title">{props.title}</h2></div>
                )}
                {props.cta && (
                    <div className="tile-slider__header__cta-container">
                        <TextButton
                            customButton
                            to={props.cta.url}
                            target=""
                            className={ctaClassList.join(" ")}
                        >
                            {props.cta.title}
                        </TextButton>
                    </div>
                )}
            </div>

            {props.tiles && props.tiles.length ? (
                <ScrollContainer
                    className="tile-slider__scroller"
                    hideScrollbars={true}
                    vertical={false}
                    onScroll={updateScrollNavigation}
                    innerRef={scrollRef}
                >
                    <div className="tile-slider__inner row">
                        {props.tiles.map((tile, tileIndex): React.Node => {
                            const referenceContent = tile.acf
                                ? getContentReferenceTileProps(tile)
                                : tile.tile_reference
                                ? getContentReferenceTileProps(
                                      addACFData(tile.tile_reference)
                                  )
                                : null;
                            const manualContent =
                                tile.manual_content && tile.tile_content
                                    ? tile.tile_content
                                    : null;

                            if (tile?.ad_tiles && Array.isArray(tile?.ads)) {
                                const adTiles = tile.ads
                                    .map(
                                        (adTile, adTileIndex): React.Node => {
                                            const referenceContent =
                                                getContentReferenceTileProps(
                                                    addACFData(
                                                        adTile.ad_tile_references
                                                    )
                                                );
                                            if (!referenceContent) return null;
                                            return <Tile {...referenceContent} />;
                                        }
                                    )
                                    .filter(Boolean);
                                return (
                                    <div
                                        key={tileIndex}
                                        className={`tile-slider__inner__item col- ${
                                            tileIndex === activeTile
                                                ? "is-active"
                                                : ""
                                        }`}
                                    >
                                        <TileCarousel
                                            duration={5000}
                                            tiles={adTiles}
                                        />
                                    </div>
                                );
                            } else if (manualContent || referenceContent) {
                                return (
                                    <div
                                        key={tileIndex}
                                        className={`tile-slider__inner__item col- ${
                                            tileIndex === activeTile
                                                ? "is-active"
                                                : ""
                                        }`}
                                    >
                                        <Tile
                                            {...(manualContent ||
                                                referenceContent)}
                                        />
                                    </div>
                                );
                            } else {
                                return null;
                            }
                        })}
                    </div>
                </ScrollContainer>
            ) : null}

            <div className="arrow-button-group">
                {showPrevArrow ? (
                    <button
                        aria-label="arrow-left"
                        className={`arrow-button-group__left`}
                        onClick={handlePrevClick}
                    >
                        <Icon name="stem-arrow-left" />
                    </button>
                ) : null}

                {showNextArrow ? (
                    <button
                        aria-label="arrow-right"
                        className={`arrow-button-group__right`}
                        onClick={handleNextClick}
                    >
                        <Icon name="stem-arrow-right" />
                    </button>
                ) : null}
            </div>
        </div>
    );
};

const TileSlider = (props: PropsType): React.Node => {
    // const [initialX, setInitialX] = React.useState(null);
    // const scrollTolerance = 40;

    // const handleTouchStart = e => {
    //     const touch = e.touches[0] || e.changedTouches[0],
    //         x = touch.pageX;

    //     setInitialX(x);
    // };

    // const handleTouchMove = e => {
    //     const touch = e.touches[0] || e.changedTouches[0],
    //         x = touch.pageX;

    //     if (initialX) {
    //         if (
    //             x > initialX + scrollTolerance ||
    //             x < initialX - scrollTolerance
    //         ) {
    //             e.preventDefault();
    //         }
    //     }
    // };

    if (!props.tiles || props.tiles.length === 0) {
        return null;
    }

    if (props.useContainer) {
        return (
            <div
                className="tile-slider__container"
                // onTouchStart={handleTouchStart}
                // onTouchMove={handleTouchMove}
            >
                <div className="container-fluid">
                    <div className="row">
                        <div className={props.colClass}>
                            <TileSliderInner {...props} />
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        return <TileSliderInner {...props} />;
    }
};

TileSlider.defaultProps = {
    itemClass: "col-xs-8 col-sm-3 col-md-3",
    colClass: "col-xs-12",
};

export default TileSlider;
